(() => {
  const config = JSON.parse(document.getElementById('CartGiftConfig').textContent);
  const cartRoot = `${window.Shopify?.routes?.root || '/'}cart`;
  const fetchCart = window.fetch.bind(window);
  let queue = Promise.resolve();

  function report(error) {
    console.error('Cart gift synchronization failed.', error);
  }

  function enqueue(action) {
    const result = queue.then(action);
    queue = result.catch(report);
    return result;
  }

  async function json(url, options) {
    const response = await fetchCart(url, options);
    const data = await response.json();
    if (!response.ok || data.errors) {
      throw new Error(data.description || data.message || `Cart request failed: ${response.status}`);
    }
    return data;
  }

  function post(action, body) {
    return json(`${cartRoot}/${action}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  const isGift = item => Number(item.product_id) === config.productId;

  async function reconcile() {
    let cart = await json(`${cartRoot}.js`);
    const gifts = cart.items.filter(isGift);
    const subtotal = cart.total_price - gifts.reduce((sum, item) => sum + item.final_line_price, 0);
    const eligible = cart.currency === config.currency && subtotal >= config.threshold;
    const retained = eligible
      ? gifts.find(item => Number(item.variant_id) === config.variantId && item.final_line_price === 0)
      : null;
    const updates = {};
    gifts.forEach(item => {
      const quantity = item === retained ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    let changed = Object.keys(updates).length > 0;
    if (changed) cart = await post('update', { updates });
    if (eligible && !retained) {
      await post('add', { items: [{ id: config.variantId, quantity: 1 }] });
      cart = await json(`${cartRoot}.js`);
      changed = true;
      const added = cart.items.find(isGift);
      if (!added || added.final_line_price !== 0) {
        if (added) await post('update', { updates: { [added.key]: 0 } });
        throw new Error('The cart gift must be available and priced at zero.');
      }
    }
    return { cart, changed };
  }

  async function sections(ids) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    const rendered = await json(url);
    if (ids.some(id => typeof rendered[id] !== 'string')) {
      throw new Error('Cart sections could not be rendered.');
    }
    return rendered;
  }

  async function refresh() {
    const targets = [];
    const cartItems = document.querySelector('cart-items');
    const mainSection = cartItems?.closest('[id^="shopify-section-"]');
    if (mainSection) {
      targets.push({ id: mainSection.id.replace('shopify-section-', ''), selector: 'cart-items' });
    }
    if (document.getElementById('CartDrawer-Body')) {
      targets.push({ id: 'cart-drawer', selector: '#CartDrawer-Body' });
    }
    if (targets.length) {
      const rendered = await sections(targets.map(target => target.id));
      targets.forEach(({ id, selector }) => {
        const replacement = new DOMParser().parseFromString(rendered[id], 'text/html').querySelector(selector);
        const current = document.querySelector(selector);
        if (!replacement || !current) throw new Error(`Missing cart section: ${id}`);
        current.innerHTML = replacement.innerHTML;
      });
    }
    window.updateCartCounters?.();
    window.updateFreeShipping?.();
  }

  async function synchronize() {
    const result = await reconcile();
    if (result.changed) await refresh();
    return result.cart;
  }

  const isAddUrl = url => /\/cart\/add(\.js)?$/.test(new URL(url, window.location.href).pathname);

  // Keep each customer mutation and its gift correction together, before callers render or redirect.
  function request(url, options) {
    let resolveEarly;
    const early = new Promise(resolve => { resolveEarly = resolve; });
    const job = enqueue(async () => {
      const response = await fetchCart(url, options);
      if (!response.ok) return response;
      const payload = await response.clone().json();
      if (payload.errors || payload.status) return response;
      // Card adds without sections can show the popup now; the gift is corrected right after.
      if (isAddUrl(url) && !payload.sections) {
        resolveEarly(response);
        const { changed } = await reconcile();
        if (changed) {
          await refresh();
          document.querySelector('cart-popup')?.updateCartCount?.();
        }
        return response;
      }
      try {
        const { cart, changed } = await reconcile();
        if (!changed) return response;
        const sectionIds = Object.keys(payload.sections || {});
        const rendered = sectionIds.length ? await sections(sectionIds) : null;
        if (!rendered) await refresh();
        const isCartResponse = Array.isArray(payload.items) && typeof payload.total_price === 'number';
        const data = isCartResponse ? cart : payload;
        if (rendered) data.sections = rendered;
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.delete('content-encoding');
        return new Response(JSON.stringify(data), { status: response.status, headers });
      } catch (error) {
        report(error);
        return response;
      }
    });
    return Promise.race([early, job]);
  }

  window.LolaCartGift = { request, sync: () => enqueue(synchronize) };
  document.addEventListener('DOMContentLoaded', () => {
    window.LolaCartGift.sync();
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) window.LolaCartGift.sync();
  });

  const approvedForms = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (approvedForms.delete(form) || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    enqueue(async () => {
      await synchronize();
      const currentForm = document.getElementById(form.id) || form;
      const submitter = currentForm.querySelector('[name="checkout"]');
      if (!currentForm.isConnected || !submitter) throw new Error('Checkout form is no longer available.');
      approvedForms.add(currentForm);
      currentForm.requestSubmit(submitter);
    });
  }, true);
})();
